// Éléments partagés des écrans Études : types, libellés, lien vers la source, drapeaux demandé / prévu / chiffré, dépôt de pièces.
import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FileText, Upload, X } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Badge, Bouton, type Ton } from '../../components/ui.tsx';
import { estLocalisationClasseur, extraitAffiche, localisationSource } from '@shared/sources.ts';
import { PRIORITES_INFO, type Priorite } from '@shared/priorites.ts';

// ─── Types (contrat de l'API du module) ───
export interface Responsabilite { action: string; party: string; party_name: string | null; note?: string | null }
export interface Exigence {
  id: string; dossier_id: string; label: string; domain: string | null; domaine_libelle: string | null; detail: string | null;
  nature: 'fait' | 'deduction' | 'question'; status: string; other_lot: string | null; other_lot_confirmed: boolean;
  in_client_request: boolean | null; in_quote: boolean | null; prevu: boolean | null; uncertain: boolean;
  detection: 'regle' | 'ia' | 'manuel'; detection_rule: string | null; prestation: string | null; exclusion: string | null;
  regle: { code: string | null; libelle: string; explication: string };
  source_version_id: string | null; source_page: number | null; source_locator: string | null; source_excerpt: string | null;
  /** Extrait source lu automatiquement (lecture optique d'une page scannée). */
  source_ocr?: boolean;
  document_id: string | null; document_title: string | null; doc_type: string | null; version_label: string | null; version_status: string | null;
  chunk_id: string | null; contexte: string | null; responsabilites: Responsabilite[];
  validated_at: string | null; validated_by_name: string | null; updated_at: string;
}
export interface SourcePreuve {
  version_id: string | null; document_id: string | null; chunk_id: string | null; titre: string; doc_type: string;
  version_label: string | null; page: number | null; locator: string | null; excerpt: string;
}
export interface Ecart {
  id: string; kind: string; title: string; description: string | null; requirement_ids: string[]; sources: SourcePreuve[]; status: string;
  /** Contrôle d'une pièce de chiffrage (validite, doublon, quantite, appareil, part_etudes, arithmetique, lecture). */
  controle?: string | null;
  /** Constat sans anomalie (arithmétique juste, part des études) : affiché, jamais compté comme écart ouvert. */
  informatif?: boolean;
  /** Priorité pour le destinataire (bloquant, important, complément) et pourquoi. */
  priorite?: Priorite; priorite_motif?: string;
}
export interface Question {
  id: string; text: string; addressee: string | null; status: string; answer: string | null; requirement_id: string | null; gap_id: string | null;
  generee: boolean; exigence: string | null; source_excerpt: string | null; source_page: number | null; source_locator: string | null;
  source_version_id: string | null; exigence_document: string | null; ecart: string | null; ecart_type: string | null;
  asked_at?: string | null; courrier_id?: string | null; courrier_numero?: string | null;   // posée par un courrier émis
  priorite?: Priorite; priorite_motif?: string;
}

/** Priorité d'un point pour le destinataire : bloquant (à obtenir avant de signer ou de chiffrer), important, complément. */
export function BadgePriorite({ priorite, motif }: { priorite: string | null | undefined; motif?: string | null }) {
  if (!priorite || !(priorite in PRIORITES_INFO)) return null;
  const p = PRIORITES_INFO[priorite as Priorite];
  const ton: Ton = priorite === 'bloquant' ? 'danger' : priorite === 'important' ? 'ambre' : 'neutre';
  return <Badge ton={ton} icone={false} titre={`${p.aide}${motif ? ` — ${motif}` : ''}`}>{p.libelle}</Badge>;
}

// ─── Libellés ───
export const TYPES_PIECE: Record<string, string> = {
  demande_client: 'Demande du client', cctp: 'CCTP', plan: 'Plan', dpgf: 'DPGF', bordereau: 'Bordereau de prix',
  devis_existant: 'Devis existant', avenant: 'Avenant (au marché)', echange: 'Échange', autre: 'Autre pièce',
};
export const NATURES: Record<string, { libelle: string; aide: string }> = {
  fait: { libelle: 'Fait', aide: 'Présent dans les pièces' },
  deduction: { libelle: 'Déduction', aide: 'Déduction technique, absente des pièces' },
  question: { libelle: 'Question', aide: 'Formulation ouverte : à faire préciser' },
};
export const ACTIONS: { code: string; libelle: string }[] = [
  { code: 'fournir', libelle: 'Fournir' }, { code: 'poser', libelle: 'Poser' }, { code: 'alimenter', libelle: 'Alimenter' },
  { code: 'raccorder', libelle: 'Raccorder' }, { code: 'programmer', libelle: 'Programmer' }, { code: 'tester', libelle: 'Tester' },
  { code: 'receptionner', libelle: 'Réceptionner' },
];
export const PARTIES: Record<string, string> = { inconnu: 'Inconnu', nous: 'Nous', client: 'Client', autre_lot: 'Autre lot', sans_objet: 'Sans objet' };
export const DESTINATAIRES: Record<string, string> = {
  client: 'Client', maitre_oeuvre: 'Maître d’œuvre', architecte: 'Architecte', autre_lot: 'Autre lot', autre: 'Autre',
};
export const TYPES_ECART: Record<string, { libelle: string; pluriel: string; ton: Ton }> = {
  piece_absente: { libelle: 'Pièce absente', pluriel: 'Pièces absentes', ton: 'danger' },
  piece_illisible: { libelle: 'Pièce illisible', pluriel: 'Pièces illisibles', ton: 'danger' },
  contradiction: { libelle: 'Contradiction', pluriel: 'Contradictions', ton: 'danger' },
  controle_chiffrage: { libelle: 'Contrôle du chiffrage', pluriel: 'Contrôles du chiffrage', ton: 'ambre' },
  oubli: { libelle: 'Oubli possible', pluriel: 'Oublis possibles', ton: 'ambre' },
  ambiguite: { libelle: 'Ambiguïté', pluriel: 'Ambiguïtés', ton: 'ambre' },
};
export const STATUTS_ECART: Record<string, string> = { ouvert: 'Ouvert', traite: 'Traité', sans_suite: 'Sans suite' };
export const STATUTS_QUESTION: Record<string, string> = { a_poser: 'À poser', posee: 'Posée', repondue: 'Répondue', sans_objet: 'Sans objet' };
export const DETECTIONS: Record<string, string> = { regle: 'Règle métier', ia: 'Assistance (à vérifier)', manuel: 'Saisie manuelle' };

export const options = (r: Record<string, string>) => Object.entries(r).map(([valeur, libelle]) => ({ valeur, libelle }));

/** Devine le type d'une pièce à partir de son nom (modifiable avant l'envoi). */
export function devinerType(nom: string) {
  const n = nom.toLowerCase();
  if (/cctp/.test(n)) return 'cctp';
  if (/dpgf|dqe/.test(n)) return 'dpgf';
  if (/bpu|bordereau/.test(n)) return 'bordereau';
  // « Avenant n° 1 », « AV1 » : avant « devis » (un avenant porte souvent le numéro du devis du marché).
  if (/avenant|(?:^|[^a-z])av[ _-]?\d/.test(n)) return 'avenant';
  if (/devis/.test(n)) return 'devis_existant';
  if (/plan|\.(png|jpe?g|webp)$/.test(n)) return 'plan';
  if (/demande|courriel|mail|\.eml$/.test(n)) return 'demande_client';
  return 'autre';
}

export const CONTROLES_CHIFFRAGE: Record<string, string> = {
  validite: 'Validité', doublon: 'Doublon possible', quantite: 'Quantités', appareil: 'Puissance et référence', part_etudes: 'Part des études',
  arithmetique: 'Arithmétique', lecture: 'Lecture du chiffrage', recapitulatif: 'Récapitulatif et détail',
  // Comparaison des pièces entre elles (CCTP / cadre ↔ devis)
  cmp_fluide: 'Fluide frigorigène', cmp_fgas: 'F-Gas (annexe IV)', cmp_performance: 'Puissance ou débit', cmp_variante: 'Matériel différent',
  cmp_quantite: 'Quantité entre pièces', cmp_quantite_securite: 'Quantité (sécurité incendie)', cmp_doublon: 'Doublon possible',
  cmp_absent: 'Absent du devis', cmp_option: 'Option non chiffrée', cmp_exclusion: 'Transfert de prestation', cmp_a_preciser: 'À préciser',
  cmp_fluide_quantite: 'Quantité de fluide', cmp_tracabilite: 'Traçabilité du fluide', cmp_dechets: 'Déchets', cmp_fluide_info: 'Équivalent CO2',
  cmp_indice: 'Indice du CCTP', cmp_rge: 'RGE', cmp_decennale: 'Assurance décennale', cmp_decennale_info: 'Assurance décennale',
  cmp_tva: 'TVA', cmp_tva_question: 'TVA',
  cmp_avenant: 'Avenant', cmp_avenant_prix: 'Avenant : prix du marché', cmp_avenant_moins_value: 'Avenant : moins-value',
  cmp_avenant_doublon: 'Avenant : doublon avec le marché', cmp_avenant_montant: 'Avenant : montant du marché',
};

/** Extrait cité, affiché avec les montants au format français (l'extrait stocké reste tel quel). */
export function Extrait({ texte, className = 'extrait' }: { texte: string | null | undefined; className?: string }) {
  if (!texte) return null;
  return <div className={className}>{extraitAffiche(texte)}</div>;
}

/** « p. 4, § 5.2 » pour un PDF ; « feuille « Devis », ligne 16 » pour un classeur (jamais « p. 1 »). */
export const repereSource = (page: number | null | undefined, locator: string | null | undefined) => localisationSource(page, locator, 'p. ');

// ─── Lien vers la source : lecteur à la bonne page, extrait repéré ───
export function LienSource({ versionId, page, chunkId, titre, localisation, compact }:
  { versionId: string | null; page: number | null; chunkId?: string | null; titre: string; localisation?: string | null; compact?: boolean }) {
  const repere = repereSource(page, localisation);
  const texte = `${titre}${repere ? `${estLocalisationClasseur(localisation) ? ', ' : ' — '}${repere}` : ''}`;
  if (!versionId) return <span className="source" title="Source sans document consultable"><FileText aria-hidden /><span>{texte}</span></span>;
  const q = new URLSearchParams();
  if (page) q.set('page', String(page));
  if (chunkId) q.set('extrait', chunkId);
  return (
    <Link className={`source ${compact ? '' : 'et-source-lien'}`} to={`/documentation/lecteur/${versionId}?${q}`} aria-label={`Ouvrir la source : ${texte}`}>
      <FileText aria-hidden /><span>{compact ? texte : `Source : ${texte}`}</span>
    </Link>
  );
}

// ─── Drapeaux demandé / prévu / chiffré (texte + icône, jamais la couleur seule) ───
function Drapeau({ libelle, v, aideInconnu }: { libelle: string; v: boolean | null; aideInconnu: string }) {
  if (v === null || v === undefined) return <Badge ton="neutre" titre={aideInconnu}>{libelle} : inconnu</Badge>;
  return v ? <Badge ton="succes">{libelle} : oui</Badge> : <Badge ton="ambre">{libelle} : non</Badge>;
}
export function Drapeaux({ e }: { e: Pick<Exigence, 'in_client_request' | 'prevu' | 'in_quote'> }) {
  return (
    <span className="et-drapeaux">
      <Drapeau libelle="Demandé" v={e.in_client_request} aideInconnu="Aucune demande du client dans le dossier" />
      <Drapeau libelle="Prévu" v={e.prevu} aideInconnu="Aucun CCTP ni plan lisible dans le dossier" />
      <Drapeau libelle="Chiffré" v={e.in_quote} aideInconnu="Aucune DPGF, aucun bordereau ni devis dans le dossier" />
    </span>
  );
}

export function BadgeIncertain() {
  return <Badge ton="ambre">Détection incertaine — à vérifier</Badge>;
}

// ─── Dépôt de pièces : glisser-déposer ET bouton, type par fichier ───
export interface PieceADeposer { fichier: File; type: string; provenance: string; versionLabel: string }

export function DepotPieces({ pieces, changer, libelle = 'Pièces du dossier', provenanceDefaut = '' }:
  { pieces: PieceADeposer[]; changer: (p: PieceADeposer[]) => void; libelle?: string; provenanceDefaut?: string }) {
  const entree = useRef<HTMLInputElement>(null);
  const [survol, setSurvol] = useState(false);
  const id = useId();
  const ajouter = (liste: FileList | null) => {
    if (!liste?.length) return;
    changer([...pieces, ...[...liste].map((f) => ({ fichier: f, type: devinerType(f.name), provenance: provenanceDefaut, versionLabel: '' }))]);
  };
  const deposer = (e: DragEvent) => { e.preventDefault(); setSurvol(false); ajouter(e.dataTransfer.files); };
  const maj = (i: number, champ: keyof PieceADeposer, v: string) => changer(pieces.map((p, j) => (j === i ? { ...p, [champ]: v } : p)));
  return (
    <div className="pile">
      <div className={`et-depot ${survol ? 'et-depot-survol' : ''}`} onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
        onDragLeave={() => setSurvol(false)} onDrop={deposer}>
        <Upload aria-hidden />
        <div className="pile" style={{ gap: 2 }}>
          <strong id={id}>{libelle}</strong>
          <span className="petit discret">Glissez les fichiers ici ou choisissez-les. PDF, images, classeurs XLSX, CSV, texte — 200 Mo au plus par fichier.</span>
        </div>
        <Bouton icone={<FileText aria-hidden />} onClick={() => entree.current?.click()} aria-describedby={id}>Choisir des fichiers</Bouton>
        <input ref={entree} type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.csv,.txt"
          onChange={(e) => { ajouter(e.target.files); e.target.value = ''; }} />
      </div>
      {pieces.length > 0 && (
        <ul className="et-pieces-a-deposer" aria-label="Fichiers à importer">
          {pieces.map((p, i) => (
            <li key={`${p.fichier.name}-${i}`}>
              <div className="et-piece-nom">
                <FileText aria-hidden className="discret" />
                <span className="mono petit">{p.fichier.name}</span>
                <span className="tres-petit discret">{Math.max(1, Math.round(p.fichier.size / 1024))} Ko</span>
                <Bouton variante="fantome" className="btn-icone" aria-label={`Retirer ${p.fichier.name}`} onClick={() => changer(pieces.filter((_, j) => j !== i))}><X aria-hidden /></Bouton>
              </div>
              <div className="et-piece-champs">
                <label className="champ"><span className="champ-libelle">Type de pièce</span>
                  <select className="saisie" value={p.type} onChange={(e) => maj(i, 'type', e.target.value)}>
                    {options(TYPES_PIECE).map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
                  </select></label>
                <label className="champ"><span className="champ-libelle">Provenance</span>
                  <input className="saisie" value={p.provenance} placeholder="Client, architecte, maître d’œuvre…" onChange={(e) => maj(i, 'provenance', e.target.value)} /></label>
                <label className="champ"><span className="champ-libelle">Version</span>
                  <input className="saisie" value={p.versionLabel} placeholder="indice A, rév. 2…" onChange={(e) => maj(i, 'versionLabel', e.target.value)} /></label>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface ResultatEnvoi { nom: string; erreur?: string; doublon?: boolean; documentId?: string }

export async function envoyerPieces(dossierId: string, pieces: PieceADeposer[], documentId?: string) {
  const fd = new FormData();
  fd.append('meta', JSON.stringify(pieces.map((p) => ({ type: p.type, provenance: p.provenance || null, versionLabel: p.versionLabel || null, documentId: documentId ?? null }))));
  for (const p of pieces) fd.append('fichiers', p.fichier, p.fichier.name);
  return (await api.envoyer<{ resultats: ResultatEnvoi[] }>(`/api/dossiers/${dossierId}/pieces`, fd)).resultats;
}

// ─── Synthèse (partagée : écran Synthèse et suivi après dépôt) ───
export interface Synthese {
  analyse: { tache: { id: string; status: string; progress: number; progress_label: string | null; error: string | null; result: any; created_at: string; finished_at: string | null } | null;
    aJour: boolean; piecesEnCours: number; lexique: number };
  pieces: { total: number; parStatut: Record<string, number>; absentes: { cle: string; titre: string; description: string }[];
    illisibles: { versionId: string; titre: string; description: string; partielle: boolean }[] };
  exigences: { total: number; parStatut: Record<string, number>; parNature: Record<string, number>; incertaines: number; nonChiffrees: number; horsDemande: number; parDetection: Record<string, number> };
  ecarts: Record<string, { ouverts: number; total: number }>;
  questions: { parStatut: Record<string, number>; aPoser: { id: string; text: string; addressee: string | null }[] };
  ia: { disponible: boolean; documentsClientsAutorises: boolean; local: boolean; raison: string | null };
  prochainesActions: { texte: string; lien: string }[];
}

export function useSynthese(dossierId: string) {
  return useQuery({
    queryKey: ['etudes-synthese', dossierId],
    queryFn: () => api.get<Synthese>(`/api/dossiers/${dossierId}/synthese`),
    refetchInterval: (q) => {
      const s = q.state.data;
      return s && (s.analyse.piecesEnCours > 0 || ['en_file', 'en_cours', 'annulation_demandee'].includes(s.analyse.tache?.status ?? '')) ? 1500 : false;
    },
  });
}

export const traitementEnCours = (s?: Synthese) => !!s && (s.analyse.piecesEnCours > 0 || ['en_file', 'en_cours'].includes(s.analyse.tache?.status ?? ''));

export function Ligne({ libelle, children }: { libelle: ReactNode; children: ReactNode }) {
  return <div className="et-ligne"><dt>{libelle}</dt><dd>{children}</dd></div>;
}
